// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BrandsPageContext } from "@insite/content-library/Pages/BrandsPage";
import Select, { SelectProps } from "@insite/mobius/Select";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadBrandCategories from "@mcmc/App/Handlers/Brands/LoadBrandCategories";
import loadBrandSolutions from "@mcmc/App/Handlers/Brands/LoadBrandSolutions";
import setSelectedBrandViewType from "@mcmc/App/Handlers/Brands/SetSelectedBrandViewType";
import setSelectedSolutionParameters from "@mcmc/App/Handlers/Brands/SetSelectedSolutionParameters";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import {
    getBrandCategories,
    getBrandCategoriesLoadingState,
    getBrandSolutions,
    getBrandSolutionsLoadingState,
    getSelectedBrandViewType,
} from "@mcmc/App/Store/Selectors/CustomBrandSelectors";
import { screen } from "@mcmc/XDL/Helpers";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    brandSolutions: getBrandSolutions(state),
    brandCategories: getBrandCategories(state),
    isBrandSolutionsLoading: getBrandSolutionsLoadingState(state),
    isBrandCategoriesLoading: getBrandCategoriesLoadingState(state),
    selectedBrandViewType: getSelectedBrandViewType(state),
});

const mapDispatchToProps = {
    loadBrandSolutions,
    loadBrandCategories,
    setSelectedBrandViewType,
    setSelectedSolutionParameters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface BrandSelectionStyles {
    wrapper?: InjectableCss;
    brandSelectionInputsWrapper?: InjectableCss;
    selectInput?: SelectProps;
}

export const brandSelectionStyles: BrandSelectionStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            @media print {
                display: none;
            }
        `,
    },
    brandSelectionInputsWrapper: {
        css: css`
            display: flex;
            flex: 1;
            margin: -15px;
            max-width: 400px;
            ${screen("lte", "xs")`
                flex-direction: column;
                max-width: none;
            `}
        `,
    },
    selectInput: {
        cssOverrides: {
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                padding: 15px;
                flex: 1;
            `,
        },
    },
};

const styles = brandSelectionStyles;

const BrandSelection: FC<Props> = ({
    brandCategories,
    loadBrandCategories,
    brandSolutions,
    loadBrandSolutions,
    isBrandSolutionsLoading,
    selectedBrandViewType,
    setSelectedSolutionParameters,
    isBrandCategoriesLoading,
    setSelectedBrandViewType,
}: Props) => {
    // *** MCMC-1590: Hide the Soltions Filter
    // useEffect(() => {
    //     if (!brandSolutions && !isBrandSolutionsLoading) {
    //         loadBrandSolutions({});
    //     }
    // }, []);

    const handleChangeSelectTypeOfLineCard = (value: string) => {
        if (value === "productCategorySelect" && !brandCategories && !isBrandCategoriesLoading) {
            loadBrandCategories({});
        }
        setSelectedBrandViewType(value);
    };

    const handleChangeSelectSolution = (value: string) => {
        const parameter = {
            sort: "name asc",
            select: "id,detailPagePath,name,logoSmallImagePath,logoAltText",
            pageSize: 500,
            page: 1,
            solution: value,
        };
        setSelectedSolutionParameters(parameter);
    };

    const disableSelectSolution =
        isBrandSolutionsLoading ||
        !brandSolutions ||
        brandSolutions.solutionsCollection.length === 0 ||
        selectedBrandViewType === "productCategorySelect";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.brandSelectionInputsWrapper}>
                <Select
                    onChange={event => handleChangeSelectTypeOfLineCard(event.currentTarget.value)}
                    label={translate("Type of Line Card")}
                    {...styles.selectInput}
                >
                    {
                        // The default value for the select is the alphabetical order
                    }
                    <option value="alphabeticalSelect">{translate("Select Type of Line Card")}</option>
                    <option value="alphabeticalSelect">{translate("Alphabetical by Manufacturer")}</option>
                    <option value="productCategorySelect">{translate("Product Category by Manufacturer")}</option>
                </Select>
                {/* <Select
                    onChange={event => handleChangeSelectSolution(event.currentTarget.value)}
                    disabled={disableSelectSolution}
                    label={translate("Solution")}
                    {...styles.selectInput}
                >
                    {
                        // The empty value will display the all brands withoud filters
                    }
                    <option value="">{translate("Select Solution")}</option>
                    {brandSolutions?.solutionsCollection?.map(solutionItem => (
                        <option value={solutionItem.value} key={solutionItem.value}>
                            {solutionItem.displayName}
                        </option>
                    ))}
                </Select> */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandSelection),
    definition: {
        group: "Brands",
        allowedContexts: [BrandsPageContext],
    },
};

export default widgetModule;
